import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, AuthContextType, AuthEvent, AuthTokens } from './types';
import { 
  isAuthenticated, 
  validateTokensForApiCall, 
  executeRefresh, 
  getTokens, 
  clearTokens, 
  setupCrossTabSync,
  isRefreshTokenExpiringSoon} from './tokenUtils';
import { 
  login,
  logout,
  refreshToken
} from '../api/auth';
import { AUTH_CONFIG } from './config';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null});
  
  // Unified refresh function used across the provider
  const refreshViaApi = useCallback(async (): Promise<AuthTokens> => {
    await refreshToken();
    const tokens = getTokens();
    if (!tokens) {
      throw new Error('No tokens found after refresh');
    }
    return tokens;
  }, []);

  // Initialize authentication state
  const initializeAuth = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      // Check if tokens exist and are valid
      if (!isAuthenticated()) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: null});
        return;
      }

      // Use token validation flow to check and refresh if needed
      const tokensValid = await validateTokensForApiCall(
        refreshViaApi,
        () => {
          // Handle logout during initialization
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            error: null});
        }
      );

      if (!tokensValid) {
        return;
      }

      // Tokens are valid, user is authenticated
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        error: null});

    } catch (error) {
      console.error('Auth initialization failed:', error);
      clearTokens();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: 'Authentication initialization failed'});
    }
  }, [refreshViaApi]);

  // Login function
  const handleLogin = useCallback(async (email: string, password: string, extendedSession: boolean): Promise<{ emailVerified: boolean; email: string }> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await login(email, password, extendedSession);

      // Get tokens from storage (AuthService should have set them)
      const tokens = getTokens();
      if (!tokens) {
        throw new Error('No tokens received after login');
      }

      // Login successful - tokens are stored
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        error: null});

      // Note: Cross-tab events are handled by setTokens() in tokenUtils
      return result;
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed'}));
      throw error;
    }
  }, []);

  // Refresh tokens function
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    try {
      const refreshed = await executeRefresh(refreshViaApi);

      if (refreshed) {
        // Tokens refreshed successfully
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: true}));
      } else {
        // Refresh failed, clear state
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: null});
      }

      return refreshed;
    } catch (error) {
      console.error('Token refresh error:', error);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: 'Session expired'});
      return false;
    }
  }, [refreshViaApi]);

  // Logout function
  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if backend call fails
    } finally {
      // Clear local state and tokens
      clearTokens();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: null});
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  // Handle cross-tab authentication events
  const handleAuthEvent = useCallback((event: AuthEvent, data?: any) => {
    switch (event) {
      case AuthEvent.LOGOUT:
      case AuthEvent.SESSION_EXPIRED:
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: event === AuthEvent.SESSION_EXPIRED ? 'Session expired' : null});
        break;
      
      case AuthEvent.LOGIN:
      case AuthEvent.TOKEN_REFRESH:
        // Re-initialize auth state when tokens change in other tabs
        initializeAuth();
        break;
    }
  }, [initializeAuth]);

  // Set up automatic token refresh interval
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const interval = setInterval(async () => {
      // Proactively rotate refresh token if it is expiring soon
      const existingTokens = getTokens();
      if (existingTokens && isRefreshTokenExpiringSoon(existingTokens.refreshToken)) {
        await executeRefresh(refreshViaApi);
      }

      // Use token validation flow for background refresh
      await validateTokensForApiCall(
        refreshViaApi,
        () => {
          // Logout if refresh token expires during background check
          handleLogout();
        }
      );
    }, AUTH_CONFIG.refreshCheckIntervalMs); // Configurable background interval

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, handleLogout, refreshViaApi]);

  // Initialize authentication on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Set up cross-tab synchronization
  useEffect(() => {
    const cleanup = setupCrossTabSync(handleAuthEvent);
    return cleanup;
  }, [handleAuthEvent]);

  // Context value
  const contextValue: AuthContextType = {
    ...authState,
    login: handleLogin,
    logout: handleLogout,
    refreshTokens,
    clearError
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 